import { useState, useEffect, useRef } from 'react';
import client from '../api/client';

export default function Kin() {
  const [tab, setTab] = useState<'accounts' | 'answers' | 'urls' | 'schedule' | 'logs'>('accounts');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [answers, setAnswers] = useState<any[]>([]);
  const [urls, setUrls] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [currentJob, setCurrentJob] = useState<any>(null);

  // 계정 폼
  const [accType, setAccType] = useState('answer');
  const [accId, setAccId] = useState('');
  const [accPw, setAccPw] = useState('');
  const [accMemo, setAccMemo] = useState('');

  // 원고 폼
  const [ansContent, setAnsContent] = useState('');
  const [ansMemo, setAnsMemo] = useState('');
  const [editingAns, setEditingAns] = useState<any>(null);
  const [likeKeywords, setLikeKeywords] = useState('');

  // URL 폼
  const [urlInput, setUrlInput] = useState('');
  const [kwInput, setKwInput] = useState('');
  const [bulkInput, setBulkInput] = useState('');

  // 스케줄 폼
  const [scheduleType, setScheduleType] = useState('once');
  const [jobType, setJobType] = useState('both');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleHour, setScheduleHour] = useState('03:00');

  const logRef = useRef<HTMLDivElement>(null);

  const loadAll = async () => {
    try {
      const [a, ans, u, s, st] = await Promise.all([
        client.get('/kin/accounts'),
        client.get('/kin/answers'),
        client.get('/kin/urls'),
        client.get('/kin/schedules'),
        client.get('/kin/settings'),
      ]);
      setAccounts(a.data.accounts || []);
      setAnswers(ans.data.answers || []);
      setUrls(u.data.urls || []);
      setSchedules(s.data.schedules || []);
      if (st.data.settings?.like_keywords) setLikeKeywords(st.data.settings.like_keywords);
    } catch {}
  };

  const loadLogs = async () => {
    try {
      const res = await client.get('/kin/logs/latest');
      setLogs(res.data.logs || []);
    } catch {}
  };

  useEffect(() => {
    loadAll();
    const interval = setInterval(() => {
      loadLogs();
      loadAll();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const s: any = {
    page: { minHeight: '100vh', background: '#0F1117', padding: 24, color: '#E8ECF4' },
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    input: { background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, width: '100%', boxSizing: 'border-box' as const },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 20px', color: '#0A1F18', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    btnRed: { background: 'none', border: '0.5px solid #F5656544', borderRadius: 6, padding: '5px 12px', color: '#F56565', fontSize: 12, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '5px 12px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
    tab: (active: boolean) => ({ background: active ? '#1A2035' : 'none', border: active ? '0.5px solid #00D4AA' : '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: active ? '#00D4AA' : '#8892A4', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 }),
    label: { color: '#8892A4', fontSize: 11, display: 'block', marginBottom: 4 },
  };

  // 즉시 실행
  const handleRun = async (type: string) => {
    try {
      const res = await client.post('/kin/jobs', { job_type: type });
      setCurrentJob(res.data.job_id);
      alert(`작업 생성 완료! 익스텐션에서 실행됩니다.`);
      setTab('logs');
    } catch (e: any) {
      alert(e.response?.data?.detail || '실패');
    }
  };

  // 스케줄 등록
  const handleSchedule = async () => {
    let run_at = '';
    if (scheduleType === 'once') {
      if (!scheduleTime) return alert('날짜/시간을 선택하세요');
      run_at = scheduleTime;
    } else {
      const today = new Date().toISOString().split('T')[0];
      run_at = `${today}T${scheduleHour}:00`;
    }
    try {
      await client.post('/kin/schedules', { job_type: jobType, schedule_type: scheduleType, run_at });
      await loadAll();
      alert('예약 완료!');
    } catch (e: any) {
      alert(e.response?.data?.detail || '실패');
    }
  };

  const statusColor = (s: string) => {
    if (s === 'success') return '#00D4AA';
    if (s === 'fail') return '#F56565';
    if (s === 'skip') return '#F6AD55';
    return '#4A5568';
  };

  return (
    <div style={s.page}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ color: '#F56565', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', marginBottom: 4 }}>PRIVATE</div>
          <div style={{ color: '#E8ECF4', fontSize: 20, fontWeight: 700 }}>지식인 봇 관리</div>
        </div>

        {/* 즉시 실행 버튼 */}
        <div style={{ ...s.card, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' as const }}>
          <span style={{ color: '#8892A4', fontSize: 13 }}>즉시 실행:</span>
          {[['answer', '📝 답변 작업'], ['like', '👍 좋아요 작업'], ['both', '🔄 통합 작업']].map(([type, label]) => (
            <button key={type} style={s.btn} onClick={() => handleRun(type)}>{label}</button>
          ))}
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' as const }}>
          {[['accounts', '👤 계정'], ['answers', '📝 원고'], ['urls', '🔗 URL'], ['schedule', '⏰ 스케줄'], ['logs', '📋 로그']].map(([key, label]) => (
            <button key={key} style={s.tab(tab === key)} onClick={() => setTab(key as any)}>{label}</button>
          ))}
        </div>

        {/* 계정 탭 */}
        {tab === 'accounts' && (
          <>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>계정 추가</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <div>
                  <label style={s.label}>유형</label>
                  <select value={accType} onChange={e => setAccType(e.target.value)}
                    style={{ ...s.input, cursor: 'pointer' }}>
                    <option value="answer">답변 계정</option>
                    <option value="like">좋아요 계정</option>
                  </select>
                </div>
                <div>
                  <label style={s.label}>아이디</label>
                  <input style={s.input} value={accId} onChange={e => setAccId(e.target.value)} placeholder="네이버 아이디" />
                </div>
                <div>
                  <label style={s.label}>비밀번호</label>
                  <input style={s.input} type="password" value={accPw} onChange={e => setAccPw(e.target.value)} placeholder="비밀번호" />
                </div>
                <div>
                  <label style={s.label}>메모</label>
                  <input style={s.input} value={accMemo} onChange={e => setAccMemo(e.target.value)} placeholder="메모 (선택)" />
                </div>
                <button style={s.btn} onClick={async () => {
                  if (!accId || !accPw) return alert('아이디/비밀번호 필수');
                  await client.post('/kin/accounts', { type: accType, naver_id: accId, naver_pw: accPw, memo: accMemo });
                  setAccId(''); setAccPw(''); setAccMemo('');
                  await loadAll();
                }}>추가</button>
              </div>
            </div>

            <div style={s.card}>
              <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1A2035' }}>
                    {['유형', '아이디', '메모', '삭제'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left' as const, color: '#8892A4', fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.id} style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ color: a.type === 'answer' ? '#00D4AA' : '#F6AD55', fontWeight: 600 }}>
                          {a.type === 'answer' ? '답변' : '좋아요'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#E8ECF4' }}>{a.naver_id}</td>
                      <td style={{ padding: '8px 12px', color: '#8892A4' }}>{a.memo}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <button style={s.btnRed} onClick={async () => {
                          await client.delete(`/kin/accounts/${a.id}`);
                          await loadAll();
                        }}>삭제</button>
                      </td>
                    </tr>
                  ))}
                  {accounts.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center' as const, color: '#4A5568' }}>등록된 계정이 없습니다</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* 원고 탭 */}
        {tab === 'answers' && (
          <>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>식별 키워드</div>
              <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 8 }}>우리 댓글에 항상 포함되는 키워드 (중복체크 및 좋아요 기준, 쉼표로 구분)</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...s.input, flex: 1 }} value={likeKeywords}
                  onChange={e => setLikeKeywords(e.target.value)}
                  placeholder="예: 셀러랭키, sellerranky" />
                <button style={s.btn} onClick={async () => {
                  await client.post('/kin/settings', { key: 'like_keywords', value: likeKeywords });
                  alert('저장 완료!');
                }}>저장</button>
              </div>
            </div>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                {editingAns ? '원고 수정' : '원고 추가'}
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={s.label}>답변 내용</label>
                <textarea style={{ ...s.input, height: 120, resize: 'vertical' as const }}
                  value={ansContent} onChange={e => setAnsContent(e.target.value)}
                  placeholder="지식인에 남길 답변 내용을 입력하세요" />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input style={{ ...s.input, flex: 1 }} value={ansMemo} onChange={e => setAnsMemo(e.target.value)} placeholder="메모 (선택)" />
                <button style={s.btn} onClick={async () => {
                  if (!ansContent) return alert('내용 필수');
                  if (editingAns) {
                    await client.put(`/kin/answers/${editingAns.id}`, { content: ansContent, memo: ansMemo });
                    setEditingAns(null);
                  } else {
                    await client.post('/kin/answers', { content: ansContent, memo: ansMemo });
                  }
                  setAnsContent(''); setAnsMemo('');
                  await loadAll();
                }}>{editingAns ? '수정 완료' : '추가'}</button>
                {editingAns && (
                  <button style={s.btnGhost} onClick={() => { setEditingAns(null); setAnsContent(''); setAnsMemo(''); }}>취소</button>
                )}
              </div>
            </div>

            {answers.map(a => (
              <div key={a.id} style={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ color: '#4A5568', fontSize: 11 }}>#{a.id} {a.memo && `· ${a.memo}`} · 사용 {a.use_count}회</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={s.btnGhost} onClick={() => { setEditingAns(a); setAnsContent(a.content); setAnsMemo(a.memo); setTab('answers'); }}>수정</button>
                    <button style={s.btnRed} onClick={async () => { await client.delete(`/kin/answers/${a.id}`); await loadAll(); }}>삭제</button>
                  </div>
                </div>
                <div style={{ color: '#E8ECF4', fontSize: 13, whiteSpace: 'pre-wrap' as const, lineHeight: 1.6 }}>{a.content}</div>
              </div>
            ))}
            {answers.length === 0 && <div style={{ ...s.card, textAlign: 'center' as const, color: '#4A5568', padding: 40 }}>등록된 원고가 없습니다</div>}
          </>
        )}

        {/* URL 탭 */}
        {tab === 'urls' && (
          <>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>URL 추가</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 16 }}>
                <div>
                  <label style={s.label}>지식인 URL</label>
                  <input style={s.input} value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://kin.naver.com/..." />
                </div>
                <div>
                  <label style={s.label}>검색 키워드</label>
                  <input style={s.input} value={kwInput} onChange={e => setKwInput(e.target.value)} placeholder="키워드" />
                </div>
                <button style={{ ...s.btn, alignSelf: 'flex-end' }} onClick={async () => {
                  if (!urlInput || !kwInput) return alert('URL과 키워드 필수');
                  await client.post('/kin/urls', { url: urlInput, keyword: kwInput });
                  setUrlInput(''); setKwInput('');
                  await loadAll();
                }}>추가</button>
              </div>

              <div style={{ borderTop: '0.5px solid #2A3045', paddingTop: 16 }}>
                <label style={s.label}>일괄 추가 (URL탭키워드 또는 URL,키워드 형식으로 줄바꿈 구분)</label>
                <textarea style={{ ...s.input, height: 100, resize: 'vertical' as const, marginBottom: 8 }}
                  value={bulkInput} onChange={e => setBulkInput(e.target.value)}
                  placeholder={"https://kin.naver.com/qna/...	키워드1\nhttps://kin.naver.com/qna/...	키워드2"} />
                <button style={s.btn} onClick={async () => {
                  const lines = bulkInput.split('\n').filter(l => l.trim());
                  const items = lines.map(l => {
                    const parts = l.includes('\t') ? l.split('\t') : l.split(',');
                    return { url: parts[0]?.trim(), keyword: parts[1]?.trim() };
                  }).filter(i => i.url && i.keyword);
                  if (!items.length) return alert('올바른 형식으로 입력하세요');
                  await client.post('/kin/urls/bulk', { items });
                  setBulkInput('');
                  await loadAll();
                  alert(`${items.length}개 추가 완료`);
                }}>일괄 추가</button>
              </div>
            </div>

            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>
                  URL 목록 ({urls.length}개)
                  <span style={{ color: '#4A5568', fontSize: 12, marginLeft: 8 }}>
                    대기 {urls.filter(u => u.status === 'pending').length} /
                    성공 {urls.filter(u => u.status === 'success').length} /
                    실패 {urls.filter(u => u.status === 'fail').length}
                  </span>
                </div>
                <button style={s.btnGhost} onClick={async () => {
                  if (!window.confirm('모든 URL 상태를 대기로 초기화할까요?')) return;
                  await client.post('/kin/urls/reset');
                  await loadAll();
                }}>⟳ 초기화</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#1A2035' }}>
                    {['키워드', 'URL', '상태', '결과', '삭제'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left' as const, color: '#8892A4' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {urls.map(u => (
                    <tr key={u.id} style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={{ padding: '8px 10px', color: '#E8ECF4' }}>{u.keyword}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <a href={u.url} target="_blank" rel="noreferrer" style={{ color: '#378ADD', fontSize: 11 }}>
                          {u.url.substring(0, 40)}...
                        </a>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ color: statusColor(u.status), fontWeight: 600 }}>{u.status}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.result_memo}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <button style={s.btnRed} onClick={async () => { await client.delete(`/kin/urls/${u.id}`); await loadAll(); }}>삭제</button>
                      </td>
                    </tr>
                  ))}
                  {urls.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center' as const, color: '#4A5568' }}>등록된 URL이 없습니다</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* 스케줄 탭 */}
        {tab === 'schedule' && (
          <>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>예약 실행</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={s.label}>작업 유형</label>
                  <select value={jobType} onChange={e => setJobType(e.target.value)} style={{ ...s.input, cursor: 'pointer' }}>
                    <option value="answer">답변 작업</option>
                    <option value="like">좋아요 작업</option>
                    <option value="both">통합 작업</option>
                  </select>
                </div>
                <div>
                  <label style={s.label}>예약 유형</label>
                  <select value={scheduleType} onChange={e => setScheduleType(e.target.value)} style={{ ...s.input, cursor: 'pointer' }}>
                    <option value="once">1회 예약</option>
                    <option value="daily">매일 반복</option>
                  </select>
                </div>
                <div>
                  <label style={s.label}>{scheduleType === 'once' ? '실행 날짜/시간' : '매일 실행 시간'}</label>
                  {scheduleType === 'once' ? (
                    <input type="datetime-local" style={s.input} value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} />
                  ) : (
                    <input type="time" style={s.input} value={scheduleHour} onChange={e => setScheduleHour(e.target.value)} />
                  )}
                </div>
              </div>
              <button style={s.btn} onClick={handleSchedule}>⏰ 예약 등록</button>
            </div>

            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>예약 목록</div>
              {schedules.length === 0 ? (
                <div style={{ color: '#4A5568', textAlign: 'center' as const, padding: 20 }}>예약된 작업이 없습니다</div>
              ) : schedules.map(sc => (
                <div key={sc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid #1E2535' }}>
                  <div>
                    <span style={{ color: '#00D4AA', fontWeight: 600, marginRight: 8 }}>
                      {sc.job_type === 'both' ? '통합' : sc.job_type === 'answer' ? '답변' : '좋아요'}
                    </span>
                    <span style={{ color: '#F6AD55', marginRight: 8 }}>
                      {sc.schedule_type === 'daily' ? '매일' : '1회'}
                    </span>
                    <span style={{ color: '#E8ECF4' }}>{sc.run_at}</span>
                  </div>
                  <button style={s.btnRed} onClick={async () => {
                    await client.delete(`/kin/schedules/${sc.id}`);
                    await loadAll();
                  }}>취소</button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* 로그 탭 */}
        {tab === 'logs' && (
          <div style={s.card}>
            <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>실행 로그</div>
            <div ref={logRef} style={{ background: '#0F1117', borderRadius: 8, padding: 16, height: 500, overflowY: 'auto' as const, fontFamily: 'monospace', fontSize: 12 }}>
              {logs.length === 0 ? (
                <div style={{ color: '#4A5568' }}>로그가 없습니다. 익스텐션을 설치하고 작업을 실행하세요.</div>
              ) : [...logs].reverse().map((l, i) => (
                <div key={i} style={{ marginBottom: 4, color: l.message.includes('✅') ? '#00D4AA' : l.message.includes('❌') ? '#F56565' : l.message.includes('⚠️') ? '#F6AD55' : '#8892A4' }}>
                  <span style={{ color: '#4A5568', marginRight: 8 }}>{String(l.created_at).split('T')[0]}</span>
                  {l.message}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
